import React, { useState, useEffect } from 'react'
import { Grid, CircularProgress, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Icons, icons } from 'constants/images'
import { colors } from 'constants/theme'
import InputTextSearch from 'component/Input/InputTextSearch'
import * as UseStyle from './UseStyle'
import ManualApi from 'api/manual.api'
import type { ManualMenuGroup, ManualMenuChild } from 'api/manual.api'
import type { ManualIndexEntry } from './manuals/manual.types'

interface ManualNavProps {
  selectedManualId: string | null
  onSelectManual: (id: string) => void
}

export default function ManualNav({ selectedManualId, onSelectManual }: ManualNavProps) {
  const { t } = useTranslation()
  const [searchInput, setSearchInput] = useState('')
  const [menuTree, setMenuTree] = useState<ManualMenuGroup[]>([])
  const [manualIndex, setManualIndex] = useState<ManualIndexEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([ManualApi.getMenuTree(), ManualApi.getManualIndex()])
      .then(([tree, index]) => {
        if (!cancelled) {
          setMenuTree(tree)
          setManualIndex(index)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const handleSearch = (value: string) => {
    setSearchInput(value)
  }

  /** Find manuals matching menu keys from the index */
  const findManualsByMenu = (menuFirstKey: string, menuSecondKey?: string, menuThirdKey?: string): ManualIndexEntry[] => {
    return manualIndex.filter((m) => {
      if (m.menuFirstKey !== menuFirstKey) return false
      if (menuSecondKey && m.menuSecondKey !== menuSecondKey) return false
      if (menuThirdKey && m.menuThirdKey !== menuThirdKey) return false
      return true
    })
  }

  /** Check if a manual exists for this leaf node */
  const getManualId = (menuFirstKey: string, menuSecondKey?: string, menuThirdKey?: string): string | null => {
    const matches = findManualsByMenu(menuFirstKey, menuSecondKey, menuThirdKey)
    return matches.length > 0 ? matches[0].id : null
  }

  /** Check if search term matches menu label or manual title */
  const matchesSearch = (labelKey: string, menuFirstKey: string, menuSecondKey?: string, menuThirdKey?: string): boolean => {
    if (!searchInput) return true
    const term = searchInput.toLowerCase()
    const label = labelKey.toLowerCase()
    if (label.includes(term)) return true
    const manuals = findManualsByMenu(menuFirstKey, menuSecondKey, menuThirdKey)
    return manuals.some((m) => m.title.toLowerCase().includes(term))
  }

  /** Filter children that match search */
  const filterChildren = (menuFirstKey: string, children: ManualMenuChild[]) => {
    if (!searchInput) return children
    return children.filter((child) => {
      if (matchesSearch(child.labelKey, menuFirstKey, child.menuSecondKey)) return true
      if (child.items) {
        return child.items.some((item) =>
          matchesSearch(item.labelKey, menuFirstKey, child.menuSecondKey, item.menuThirdKey)
        )
      }
      return false
    })
  }

  const renderLeafItem = (
    label: string,
    manualId: string | null,
    isActive: boolean
  ) => (
    <li
      onClick={(e) => { e.stopPropagation(); manualId && onSelectManual(manualId) }}
      style={{
        position: 'relative',
        zIndex: 2,
        cursor: manualId ? 'pointer' : 'default',
        color: isActive ? colors.themeSecondColor : manualId ? colors.black : '#bbb',
        opacity: manualId ? 1 : 0.5,
      }}
    >
      {isActive ? (
        <span className="pr-2"><Icons.AC_DOT /></span>
      ) : (
        <img src={icons.black_dot} alt="dot" className="pr-2" />
      )}
      {label}
    </li>
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={32} sx={{ color: colors.themeSecondColor }} />
      </Box>
    )
  }

  return (
    <div>
      <Grid item xs={12}>
        <UseStyle.TextHeader>
          {t('UPDATEPAGE.BUTTON.MANUAL')}
        </UseStyle.TextHeader>
        <div className="px-3 w-100 pb-3">
          <InputTextSearch
            keyInput="search-manual"
            onchange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
            value={searchInput}
            onClear={() => handleSearch('')}
          />
        </div>

        {menuTree.map((group) => {
          const filteredChildren = filterChildren(group.menuFirstKey, group.children)
          if (searchInput && filteredChildren.length === 0) return null

          return (
            <UseStyle.SlideDataContainer key={group.menuFirstKey} defaultExpanded={false} sx={{ '&::before': { display: 'none' } }}>
              <UseStyle.SlideData aria-controls={`manual-${group.menuFirstKey}-content`}>
                <div className="d-flex flex-row justify-content-start align-items-center">
                  <span className="pr-2"><Icons.Manual /></span>
                  <span style={{ color: colors.black }}>{group.labelKey}</span>
                </div>
              </UseStyle.SlideData>

              <UseStyle.ListUpdate>
                {filteredChildren.length === 0 && group.children.length === 0 ? (
                  <UseStyle.ManualLiast sx={{ m: 0, pl: 4 }}>
                    {(() => {
                      const manualId = getManualId(group.menuFirstKey)
                      const isActive = selectedManualId === manualId
                      return renderLeafItem(group.labelKey, manualId, isActive)
                    })()}
                  </UseStyle.ManualLiast>
                ) : (
                  filteredChildren.map((child) => {
                    if (child.items && child.items.length > 0) {
                      const filteredItems = searchInput
                        ? child.items.filter((item) =>
                            matchesSearch(item.labelKey, group.menuFirstKey, child.menuSecondKey, item.menuThirdKey)
                          )
                        : child.items

                      return (
                        <UseStyle.SlideDataContainer key={child.menuSecondKey} defaultExpanded={false} sx={{ '&::before': { display: 'none' } }}>
                          <UseStyle.SlideData aria-controls={`manual-${child.menuSecondKey}-content`}>
                            <div className="d-flex flex-row justify-content-start align-items-center">
                              <span style={{ color: colors.black, fontSize: 14 }}>{child.labelKey}</span>
                            </div>
                          </UseStyle.SlideData>
                          <UseStyle.ListUpdate>
                            <UseStyle.ManualLiast sx={{ m: 0, pl: 4 }}>
                              {filteredItems.map((item) => {
                                // items inside sub-accordion
                                const manualId = getManualId(group.menuFirstKey, child.menuSecondKey, item.menuThirdKey)
                                const isActive = selectedManualId === manualId
                                return (
                                  <React.Fragment key={item.menuThirdKey}>
                                    {renderLeafItem(item.labelKey, manualId, isActive)}
                                  </React.Fragment>
                                )
                              })}
                            </UseStyle.ManualLiast>
                          </UseStyle.ListUpdate>
                        </UseStyle.SlideDataContainer>
                      )
                    }

                    const manualId = getManualId(group.menuFirstKey, child.menuSecondKey)
                    const isActive = selectedManualId === manualId
                    return (
                      <UseStyle.ManualLiast sx={{ m: 0, pl: 2 }} key={child.menuSecondKey}>
                        {renderLeafItem(child.labelKey, manualId, isActive)}
                      </UseStyle.ManualLiast>
                    )
                  })
                )}
              </UseStyle.ListUpdate>
            </UseStyle.SlideDataContainer>
          )
        })}
      </Grid>
    </div>
  )
}
